import { pianoNote, sfx } from '../../audio/sfx';
import { apply, celebrate, onceToday, toast } from '../../core/effects';
import { S } from '../../core/game';
import { m } from '../../i18n';
import { defineExternal, defineGame } from '../../ui/arcade';
import { h } from '../../ui/dom';
import { openBlocks } from '../../ui/blocks';
import { openPuzzle } from '../../ui/puzzle';
import { rr, text, type ArcadeGame, type Button } from '../game';

// ---------------------------------------------------------------------------
// Piano rhythm: four lanes of falling notes
// ---------------------------------------------------------------------------
function rhythm(): ArcadeGame {
  const W = 240, H = 360, LINE = 310, LANE_X = [36, 92, 148, 204];
  const FREQ = [261.63, 329.63, 392.0, 523.25];
  const COLORS = ['#ff7aa0', '#ffd34e', '#57c278', '#7fc8ff'];
  let notes: { lane: number; y: number; hit: boolean }[] = [];
  let spawned = 0, spawnT = 0, speed = 150, combo = 0, misses = 0, fx: { lane: number; t: number; good: boolean }[] = [];
  const hit = (lane: number) => {
    const n = notes.find((q) => q.lane === lane && !q.hit && Math.abs(q.y - LINE) < 26);
    if (n) {
      n.hit = true;
      const perfect = Math.abs(n.y - LINE) < 11;
      combo++;
      g.score += perfect ? 3 + Math.min(5, Math.floor(combo / 5)) : 1;
      pianoNote(FREQ[lane]);
      fx.push({ lane, t: 0.3, good: perfect });
    } else {
      combo = 0;
      sfx('click');
    }
  };
  const g: ArcadeGame = {
    w: W, h: H, buttons: [], score: 0, over: false,
    reset() {
      notes = []; spawned = 0; spawnT = 0.6; speed = 150; combo = 0; misses = 0; fx = []; g.score = 0; g.over = false; g.won = false;
    },
    press(b: Button, down: boolean) {
      if (!down) return;
      const map: Partial<Record<Button, number>> = { left: 0, down: 1, up: 2, right: 3 };
      const lane = map[b];
      if (lane !== undefined) hit(lane);
    },
    pointer(type, x) {
      if (type === 'down') hit(Math.max(0, Math.min(3, Math.floor(x / (W / 4)))));
    },
    update(dt) {
      spawnT -= dt;
      if (spawnT <= 0 && spawned < 48) {
        spawnT = Math.max(0.28, 0.75 - spawned * 0.009);
        spawned++;
        notes.push({ lane: Math.floor(Math.random() * 4), y: -10, hit: false });
        speed += 1.5;
      }
      for (const n of notes) n.y += speed * dt;
      for (let i = notes.length - 1; i >= 0; i--) {
        const n = notes[i];
        if (n.hit || n.y > H + 10) {
          if (!n.hit) {
            misses++;
            combo = 0;
          }
          notes.splice(i, 1);
        }
      }
      fx = fx.filter((f) => (f.t -= dt) > 0);
      if (misses >= 8) g.over = true;
      if (spawned >= 48 && !notes.length) {
        g.over = true;
        g.won = true;
      }
    },
    draw(c) {
      const bg = c.createLinearGradient(0, 0, 0, H);
      bg.addColorStop(0, '#2a1a40');
      bg.addColorStop(1, '#4a2a5a');
      c.fillStyle = bg;
      c.fillRect(0, 0, W, H);
      LANE_X.forEach((x, i) => {
        c.fillStyle = 'rgba(255,255,255,0.05)';
        c.fillRect(x - 24, 0, 48, H);
        rr(c, x - 22, LINE - 8, 44, 16, 8, 'rgba(255,255,255,0.15)');
        c.strokeStyle = COLORS[i];
        c.lineWidth = 2;
        c.beginPath();
        c.roundRect(x - 22, LINE - 8, 44, 16, 8);
        c.stroke();
      });
      for (const n of notes) {
        const x = LANE_X[n.lane];
        rr(c, x - 18, n.y - 7, 36, 14, 7, COLORS[n.lane]);
        c.fillStyle = 'rgba(255,255,255,0.5)';
        c.fillRect(x - 12, n.y - 5, 24, 2);
      }
      for (const f of fx) {
        c.globalAlpha = f.t / 0.3;
        text(c, f.good ? 'PERFECT' : 'GOOD', LANE_X[f.lane], LINE - 26 - (0.3 - f.t) * 40, 11, f.good ? '#ffd84a' : '#ffffff');
        c.globalAlpha = 1;
      }
      // Piano keys at the bottom.
      for (let i = 0; i < 4; i++) rr(c, i * 60 + 2, H - 30, 56, 28, 4, fx.some((f) => f.lane === i) ? COLORS[i] : '#fbf8ef');
      if (combo > 2) text(c, `${combo} combo!`, W / 2, 30, 14, '#ffd84a');
    },
    stars: (s) => Math.min(6, Math.floor(s / 20)),
  };
  return g;
}

// ---------------------------------------------------------------------------
// Circus juggler: toss each ball as it lands in a hand
// ---------------------------------------------------------------------------
function circus(): ArcadeGame {
  const W = 280, H = 320, HAND_Y = 240, HANDS = [90, 190];
  let balls: { x: number; y: number; vx: number; vy: number; col: string; to: 0 | 1 }[] = [];
  let catches = 0;
  const COLS = ['#e2493b', '#3a8fd9', '#ffd34e', '#57c278'];
  const G = 900;
  const launch = (hand: 0 | 1, col: string) => {
    const from = HANDS[hand], to = HANDS[1 - hand];
    // Flight time shrinks as you get better; the arc always lands in the other hand.
    const T = 1.3 - Math.min(0.45, catches * 0.012);
    balls.push({ x: from, y: HAND_Y, vx: (to - from) / T, vy: (-G * T) / 2, col, to: (1 - hand) as 0 | 1 });
  };
  const toss = (hand: 0 | 1) => {
    const b = balls.find((q) => q.to === hand && q.vy > 0 && Math.abs(q.y - HAND_Y) < 26 && Math.abs(q.x - HANDS[hand]) < 30);
    if (!b) return sfx('click');
    balls.splice(balls.indexOf(b), 1);
    catches++;
    g.score = catches;
    sfx('pop');
    launch(hand, b.col);
    if (catches % 6 === 0 && balls.length < 4) launch((1 - hand) as 0 | 1, COLS[balls.length + 1]);
  };
  const g: ArcadeGame = {
    w: W, h: H, buttons: ['left', 'right'], score: 0, over: false,
    reset() {
      balls = [];
      catches = 0;
      g.score = 0;
      g.over = false;
      launch(0, COLS[0]);
    },
    press(b, down) {
      if (!down) return;
      if (b === 'left') toss(0);
      if (b === 'right') toss(1);
    },
    pointer(type, x) {
      if (type === 'down') toss(x < W / 2 ? 0 : 1);
    },
    update(dt) {
      // Gravity matched to the launch so balls arrive at the other hand.
      for (const b of balls) {
        b.vy += G * dt;
        b.x += b.vx * dt;
        b.y += b.vy * dt;
        if (b.y > HAND_Y + 40) {
          g.over = true;
          sfx('bad');
        }
      }
    },
    draw(c, t) {
      // Big top tent stripes.
      for (let i = 0; i < 10; i++) {
        c.fillStyle = i % 2 ? '#e2493b' : '#fff3e0';
        c.beginPath();
        c.moveTo(W / 2, -20);
        c.lineTo(i * 32 - 20, H);
        c.lineTo(i * 32 + 12, H);
        c.fill();
      }
      c.fillStyle = 'rgba(40,20,10,0.35)';
      c.fillRect(0, 0, W, H);
      // Spotlight.
      const g2 = c.createRadialGradient(W / 2, 220, 10, W / 2, 220, 140);
      g2.addColorStop(0, 'rgba(255,240,180,0.6)');
      g2.addColorStop(1, 'rgba(255,240,180,0)');
      c.fillStyle = g2;
      c.fillRect(0, 0, W, H);
      // An original clown: round face, blue hair tufts, star cheeks.
      const cx = W / 2, cy = 214;
      rr(c, cx - 26, cy + 8, 52, 50, 16, '#7a5ac8');
      for (let i = 0; i < 3; i++) {
        c.fillStyle = '#ffd34e';
        c.beginPath();
        c.arc(cx, cy + 20 + i * 12, 3, 0, Math.PI * 2);
        c.fill();
      }
      c.fillStyle = '#fff7ee';
      c.beginPath();
      c.arc(cx, cy - 8, 22, 0, Math.PI * 2);
      c.fill();
      c.fillStyle = '#4fc3ff';
      for (const s of [-1, 1]) {
        c.beginPath();
        c.arc(cx + s * 22, cy - 18, 10, 0, Math.PI * 2);
        c.fill();
      }
      c.fillStyle = '#e2493b';
      c.beginPath();
      c.arc(cx, cy - 6, 5, 0, Math.PI * 2);
      c.fill();
      c.fillStyle = '#2a1810';
      c.fillRect(cx - 9, cy - 14, 3, 4);
      c.fillRect(cx + 6, cy - 14, 3, 4);
      c.strokeStyle = '#e2493b';
      c.lineWidth = 2;
      c.beginPath();
      c.arc(cx, cy, 8, 0.2, Math.PI - 0.2);
      c.stroke();
      // Hands.
      for (const hx of HANDS) {
        c.fillStyle = '#ffffff';
        c.beginPath();
        c.arc(hx, HAND_Y + 6, 10, 0, Math.PI * 2);
        c.fill();
      }
      for (const b of balls) {
        c.fillStyle = b.col;
        c.beginPath();
        c.arc(b.x, b.y, 9, 0, Math.PI * 2);
        c.fill();
        c.fillStyle = 'rgba(255,255,255,0.6)';
        c.beginPath();
        c.arc(b.x - 3, b.y - 3, 3, 0, Math.PI * 2);
        c.fill();
      }
      text(c, `🎪 ${catches}`, 14, 18, 14, '#fff', 'left');
      void t;
    },
    stars: (s) => Math.min(6, Math.floor(s / 6)),
  };
  return g;
}

// ---------------------------------------------------------------------------
// Hungry caterpillar (snake)
// ---------------------------------------------------------------------------
function snake(): ArcadeGame {
  const N = 16, CELL = 18, W = N * CELL, H = N * CELL;
  let body: { x: number; y: number }[] = [];
  let dir = { x: 1, y: 0 }, next = { x: 1, y: 0 }, leaf = { x: 10, y: 8 }, acc = 0, tick = 0.16;
  const place = () => {
    do leaf = { x: Math.floor(Math.random() * N), y: Math.floor(Math.random() * N) };
    while (body.some((b) => b.x === leaf.x && b.y === leaf.y));
  };
  const turn = (d: 'left' | 'right' | 'up' | 'down') => {
    const v = { left: { x: -1, y: 0 }, right: { x: 1, y: 0 }, up: { x: 0, y: -1 }, down: { x: 0, y: 1 } }[d];
    if (v.x !== -dir.x || v.y !== -dir.y) next = v;
  };
  const g: ArcadeGame = {
    w: W, h: H, buttons: ['left', 'up', 'down', 'right'], score: 0, over: false,
    reset() {
      body = [{ x: 5, y: 8 }, { x: 4, y: 8 }, { x: 3, y: 8 }];
      dir = { x: 1, y: 0 };
      next = dir;
      tick = 0.16;
      acc = 0;
      g.score = 0;
      g.over = false;
      place();
    },
    press(b, down) {
      if (down && b !== 'a') turn(b);
    },
    swipe: turn,
    update(dt) {
      acc += dt;
      if (acc < tick) return;
      acc = 0;
      dir = next;
      const head = { x: body[0].x + dir.x, y: body[0].y + dir.y };
      if (head.x < 0 || head.y < 0 || head.x >= N || head.y >= N || body.some((b) => b.x === head.x && b.y === head.y)) {
        g.over = true;
        sfx('bad');
        return;
      }
      body.unshift(head);
      if (head.x === leaf.x && head.y === leaf.y) {
        g.score++;
        tick = Math.max(0.07, tick - 0.004);
        sfx('pickup');
        place();
      } else body.pop();
    },
    draw(c) {
      for (let y = 0; y < N; y++) for (let x = 0; x < N; x++) {
        c.fillStyle = (x + y) % 2 ? '#7cbf5a' : '#86c862';
        c.fillRect(x * CELL, y * CELL, CELL, CELL);
      }
      // Leaf.
      c.fillStyle = '#3f9a4a';
      c.beginPath();
      c.ellipse(leaf.x * CELL + 9, leaf.y * CELL + 9, 7, 4, -0.6, 0, Math.PI * 2);
      c.fill();
      c.strokeStyle = '#2a6f30';
      c.beginPath();
      c.moveTo(leaf.x * CELL + 4, leaf.y * CELL + 13);
      c.lineTo(leaf.x * CELL + 14, leaf.y * CELL + 5);
      c.stroke();
      body.forEach((b, i) => {
        const col = i === 0 ? '#ffb347' : i % 2 ? '#a6e05a' : '#8cd04a';
        c.fillStyle = col;
        c.beginPath();
        c.arc(b.x * CELL + 9, b.y * CELL + 9, i === 0 ? 9 : 8, 0, Math.PI * 2);
        c.fill();
        if (i === 0) {
          c.fillStyle = '#2a1810';
          c.fillRect(b.x * CELL + 5 + dir.x * 2, b.y * CELL + 6 + dir.y * 2, 2.5, 2.5);
          c.fillRect(b.x * CELL + 11 + dir.x * 2, b.y * CELL + 6 + dir.y * 2, 2.5, 2.5);
        }
      });
    },
    stars: (s) => Math.min(6, Math.floor(s / 4)),
  };
  return g;
}

// ---------------------------------------------------------------------------
// Mine garden (minesweeper with sleepy moles)
// ---------------------------------------------------------------------------
function minesweeper(): ArcadeGame {
  const N = 9, CELL = 32, W = N * CELL, H = N * CELL, MOLES = 10;
  let mole: boolean[] = [], open: boolean[] = [], flag: boolean[] = [], first = true, flagMode = false;
  const idx = (x: number, y: number) => y * N + x;
  const near = (x: number, y: number) => {
    let n = 0;
    for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) {
      const nx = x + dx, ny = y + dy;
      if (nx >= 0 && ny >= 0 && nx < N && ny < N && mole[idx(nx, ny)]) n++;
    }
    return n;
  };
  const reveal = (x: number, y: number) => {
    const stack = [[x, y]];
    while (stack.length) {
      const [cx, cy] = stack.pop()!;
      const i = idx(cx, cy);
      if (open[i] || flag[i]) continue;
      open[i] = true;
      if (near(cx, cy) === 0) for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) {
        const nx = cx + dx, ny = cy + dy;
        if (nx >= 0 && ny >= 0 && nx < N && ny < N) stack.push([nx, ny]);
      }
    }
  };
  let modeBtn: HTMLButtonElement | null = null;
  const g: ArcadeGame = {
    w: W, h: H, buttons: [], score: 0, over: false,
    reset() {
      mole = Array(N * N).fill(false);
      open = Array(N * N).fill(false);
      flag = Array(N * N).fill(false);
      first = true;
      g.score = 0;
      g.over = false;
      g.won = false;
    },
    pointer(type, px, py) {
      if (type !== 'down') return;
      const x = Math.floor(px / CELL), y = Math.floor(py / CELL), i = idx(x, y);
      if (x < 0 || y < 0 || x >= N || y >= N || open[i]) return;
      if (flagMode) {
        flag[i] = !flag[i];
        sfx('click');
        return;
      }
      if (flag[i]) return;
      if (first) {
        // The first dig is always safe.
        first = false;
        let placed = 0;
        while (placed < MOLES) {
          const r = Math.floor(Math.random() * N * N);
          const rx = r % N, ry = Math.floor(r / N);
          if (mole[r] || (Math.abs(rx - x) <= 1 && Math.abs(ry - y) <= 1)) continue;
          mole[r] = true;
          placed++;
        }
      }
      if (mole[i]) {
        open[i] = true;
        g.over = true;
        sfx('bad');
        return;
      }
      reveal(x, y);
      sfx('pop');
      g.score = open.filter(Boolean).length;
      if (g.score === N * N - MOLES) {
        g.score += 50;
        g.won = true;
        g.over = true;
        sfx('fanfare');
      }
    },
    update() {},
    draw(c) {
      for (let y = 0; y < N; y++) for (let x = 0; x < N; x++) {
        const i = idx(x, y), px = x * CELL, py = y * CELL;
        if (open[i]) {
          c.fillStyle = (x + y) % 2 ? '#e2c48e' : '#d8b880';
          c.fillRect(px, py, CELL, CELL);
          if (mole[i]) {
            c.fillStyle = '#7a5a3a';
            c.beginPath();
            c.arc(px + 16, py + 18, 10, 0, Math.PI * 2);
            c.fill();
            c.fillStyle = '#ffb3c6';
            c.fillRect(px + 14, py + 18, 4, 3);
            c.fillStyle = '#2a1810';
            c.fillRect(px + 11, py + 13, 3, 3);
            c.fillRect(px + 18, py + 13, 3, 3);
          } else {
            const n = near(x, y);
            if (n) text(c, String(n), px + 16, py + 17, 16, ['#3a8fd9', '#2e9a5a', '#e2493b', '#7a3aa8', '#a8261c', '#1a8a8a', '#333', '#777'][n - 1]);
          }
        } else {
          c.fillStyle = (x + y) % 2 ? '#62ab55' : '#6db85f';
          c.fillRect(px, py, CELL, CELL);
          c.fillStyle = 'rgba(255,255,255,0.15)';
          c.fillRect(px, py, CELL, 3);
          if (flag[i]) text(c, '🚩', px + 16, py + 17, 16, '#fff');
          if (g.over && mole[i]) text(c, '🐹', px + 16, py + 17, 16, '#fff');
        }
        c.strokeStyle = 'rgba(0,0,0,0.15)';
        c.strokeRect(px + 0.5, py + 0.5, CELL - 1, CELL - 1);
      }
    },
    extra() {
      const t = m().arcade.mines;
      modeBtn = h('button', {
        class: 'btn small gold wide',
        onclick: () => {
          flagMode = !flagMode;
          if (modeBtn) modeBtn.textContent = flagMode ? t.flag : t.dig;
        },
      }, flagMode ? t.flag : t.dig);
      return modeBtn;
    },
    stars: (s) => (s >= 50 ? 6 : Math.min(3, Math.floor(s / 20))),
  };
  return g;
}

// ---------------------------------------------------------------------------
// Fruit swap (match-3)
// ---------------------------------------------------------------------------
function match3(): ArcadeGame {
  const N = 7, CELL = 40, W = N * CELL, H = N * CELL + 30;
  const FRUITS = ['#e2493b', '#ff9f43', '#ffd34e', '#57c278', '#7a5ac8', '#ff7aa0'];
  let grid: number[] = [], sel = -1, moves = 25, busy = 0, pops: { i: number; t: number }[] = [];
  const rnd = () => Math.floor(Math.random() * FRUITS.length);
  const find = (): Set<number> => {
    const out = new Set<number>();
    for (let y = 0; y < N; y++) for (let x = 0; x < N; x++) {
      const v = grid[y * N + x];
      if (v < 0) continue;
      if (x < N - 2 && grid[y * N + x + 1] === v && grid[y * N + x + 2] === v) [0, 1, 2].forEach((k) => out.add(y * N + x + k));
      if (y < N - 2 && grid[(y + 1) * N + x] === v && grid[(y + 2) * N + x] === v) [0, 1, 2].forEach((k) => out.add((y + k) * N + x));
    }
    return out;
  };
  const settle = (chain = 1): void => {
    const m3 = find();
    if (!m3.size) return;
    g.score += m3.size * 10 * chain;
    m3.forEach((i) => pops.push({ i, t: 0.25 }));
    m3.forEach((i) => (grid[i] = -1));
    sfx(chain > 1 ? 'line' : 'pickup');
    for (let x = 0; x < N; x++) {
      const col = [];
      for (let y = N - 1; y >= 0; y--) if (grid[y * N + x] >= 0) col.push(grid[y * N + x]);
      for (let y = N - 1, k = 0; y >= 0; y--, k++) grid[y * N + x] = k < col.length ? col[k] : rnd();
    }
    busy = 0.2;
    settle(chain + 1);
  };
  const g: ArcadeGame = {
    w: W, h: H, buttons: [], score: 0, over: false,
    reset() {
      do grid = Array.from({ length: N * N }, rnd);
      while (find().size);
      sel = -1;
      moves = 25;
      g.score = 0;
      g.over = false;
    },
    pointer(type, px, py) {
      if (type !== 'down' || busy > 0 || py > N * CELL) return;
      const i = Math.floor(py / CELL) * N + Math.floor(px / CELL);
      if (sel < 0) {
        sel = i;
        sfx('click');
        return;
      }
      const a = sel, b = i;
      sel = -1;
      const adj = Math.abs((a % N) - (b % N)) + Math.abs(Math.floor(a / N) - Math.floor(b / N)) === 1;
      if (!adj) return;
      [grid[a], grid[b]] = [grid[b], grid[a]];
      if (!find().size) {
        [grid[a], grid[b]] = [grid[b], grid[a]];
        sfx('bad');
        return;
      }
      moves--;
      settle();
      if (moves <= 0) g.over = true;
    },
    update(dt) {
      busy = Math.max(0, busy - dt);
      pops = pops.filter((p) => (p.t -= dt) > 0);
    },
    draw(c, t) {
      c.fillStyle = '#3a2418';
      c.fillRect(0, 0, W, H);
      for (let i = 0; i < N * N; i++) {
        const x = (i % N) * CELL, y = Math.floor(i / N) * CELL;
        rr(c, x + 2, y + 2, CELL - 4, CELL - 4, 8, (Math.floor(i / N) + i) % 2 ? '#4a3020' : '#553a26');
        const v = grid[i];
        if (v < 0) continue;
        const bounce = i === sel ? Math.sin(t * 10) * 2 : 0;
        fruit(c, x + CELL / 2, y + CELL / 2 + bounce, v, FRUITS[v]);
      }
      for (const p of pops) {
        c.globalAlpha = p.t / 0.25;
        c.fillStyle = '#fff6c0';
        c.beginPath();
        c.arc((p.i % N) * CELL + 20, Math.floor(p.i / N) * CELL + 20, 18 * (1 - p.t / 0.25) + 6, 0, Math.PI * 2);
        c.fill();
        c.globalAlpha = 1;
      }
      text(c, `🍓 ${moves}`, W / 2, N * CELL + 15, 15, '#ffd84a');
    },
    stars: (s) => Math.min(6, Math.floor(s / 200)),
  };
  return g;
}

function fruit(c: CanvasRenderingContext2D, x: number, y: number, kind: number, col: string): void {
  c.fillStyle = col;
  c.beginPath();
  if (kind === 2) c.ellipse(x, y, 14, 10, -0.4, 0, Math.PI * 2);
  else if (kind === 4) {
    for (const [dx, dy] of [[-6, 4], [6, 4], [0, -4], [-3, -8], [3, -8], [0, 8]]) c.arc(x + dx, y + dy, 5.5, 0, Math.PI * 2);
  } else if (kind === 5) {
    c.moveTo(x, y + 13);
    c.bezierCurveTo(x - 16, y - 2, x - 8, y - 14, x, y - 6);
    c.bezierCurveTo(x + 8, y - 14, x + 16, y - 2, x, y + 13);
  } else c.arc(x, y, 13, 0, Math.PI * 2);
  c.fill();
  c.fillStyle = 'rgba(255,255,255,0.45)';
  c.beginPath();
  c.arc(x - 4, y - 4, 4, 0, Math.PI * 2);
  c.fill();
  if (kind !== 4) {
    c.fillStyle = '#3f9a4a';
    c.beginPath();
    c.ellipse(x + 3, y - 13, 4, 2, -0.5, 0, Math.PI * 2);
    c.fill();
  }
}

// ---------------------------------------------------------------------------
// Firefly garden: an original hedge-maze collecting game
// ---------------------------------------------------------------------------
const MAZE = [
  '###############',
  '#o....#.#....o#',
  '#.##.#...#.##.#',
  '#.#...#.#...#.#',
  '#...#.....#...#',
  '##.##.###.##.##',
  '#.....#.#.....#',
  '#.###.....###.#',
  '#...#.#=#.#...#',
  '###.#.#=#.#.###',
  '#.....#.#.....#',
  '#.##.#...#.##.#',
  '#o.#...#...#.o#',
  '##.#.#####.#.##',
  '#.............#',
  '###############',
];

function maze(): ArcadeGame {
  const C = 18, COLS = 15, ROWS = MAZE.length, W = COLS * C, H = ROWS * C + 22;
  let dots = new Set<string>(), flowers = new Set<string>();
  let px = 7, py = 14, dir = { x: 0, y: 0 }, want = { x: 0, y: 0 }, mv = 0, lives = 3, sleep = 0;
  let frogs: { x: number; y: number; d: { x: number; y: number }; mv: number; home: { x: number; y: number } }[] = [];
  const wall = (x: number, y: number) => MAZE[y]?.[x] === '#' || MAZE[y]?.[x] === undefined;
  const reset = () => {
    dots = new Set();
    flowers = new Set();
    MAZE.forEach((row, y) => [...row].forEach((ch, x) => {
      if (ch === '.') dots.add(`${x},${y}`);
      if (ch === 'o') flowers.add(`${x},${y}`);
    }));
    px = 7;
    py = 14;
    dir = { x: 0, y: 0 };
    frogs = [{ x: 7, y: 8 }, { x: 7, y: 9 }, { x: 7, y: 6 }].map((p) => ({ ...p, d: { x: 1, y: 0 }, mv: 0, home: { ...p } }));
  };
  const turn = (d: 'left' | 'right' | 'up' | 'down') => {
    want = { left: { x: -1, y: 0 }, right: { x: 1, y: 0 }, up: { x: 0, y: -1 }, down: { x: 0, y: 1 } }[d];
  };
  const g: ArcadeGame = {
    w: W, h: H, buttons: ['left', 'up', 'down', 'right'], score: 0, over: false,
    reset() {
      reset();
      lives = 3;
      g.score = 0;
      g.over = false;
      g.won = false;
    },
    press(b, down) {
      if (down && b !== 'a') turn(b);
    },
    swipe: turn,
    update(dt) {
      sleep = Math.max(0, sleep - dt);
      mv += dt;
      if (mv > 0.15) {
        mv = 0;
        if (!wall(px + want.x, py + want.y)) dir = want;
        if (!wall(px + dir.x, py + dir.y)) {
          px += dir.x;
          py += dir.y;
        }
        const k = `${px},${py}`;
        if (dots.delete(k)) {
          g.score += 10;
          if (dots.size % 4 === 0) sfx('pop');
        }
        if (flowers.delete(k)) {
          sleep = 6;
          g.score += 50;
          sfx('pickup');
        }
        if (!dots.size && !flowers.size) {
          g.score += 300;
          g.won = true;
          g.over = true;
          sfx('fanfare');
        }
      }
      for (const f of frogs) {
        f.mv += dt;
        if (f.mv < (sleep > 0 ? 0.42 : 0.26)) continue;
        f.mv = 0;
        const opts = [{ x: 1, y: 0 }, { x: -1, y: 0 }, { x: 0, y: 1 }, { x: 0, y: -1 }].filter((d) => !wall(f.x + d.x, f.y + d.y) && !(d.x === -f.d.x && d.y === -f.d.y));
        if (!opts.length) {
          f.d = { x: -f.d.x, y: -f.d.y };
          continue;
        }
        // Hop towards the firefly most of the time, otherwise at random.
        opts.sort((a, b) => Math.hypot(f.x + a.x - px, f.y + a.y - py) - Math.hypot(f.x + b.x - px, f.y + b.y - py));
        f.d = sleep > 0 ? opts[opts.length - 1] : Math.random() < 0.65 ? opts[0] : opts[Math.floor(Math.random() * opts.length)];
        f.x += f.d.x;
        f.y += f.d.y;
      }
      for (const f of frogs) {
        if (f.x === px && f.y === py) {
          if (sleep > 0) {
            g.score += 100;
            f.x = f.home.x;
            f.y = f.home.y;
            sfx('ok');
          } else {
            lives--;
            sfx('bad');
            if (lives <= 0) g.over = true;
            px = 7;
            py = 14;
            dir = { x: 0, y: 0 };
            frogs.forEach((q) => ((q.x = q.home.x), (q.y = q.home.y)));
          }
        }
      }
    },
    draw(c, t) {
      c.fillStyle = '#16301c';
      c.fillRect(0, 0, W, H);
      MAZE.forEach((row, y) => [...row].forEach((ch, x) => {
        if (ch === '#') {
          rr(c, x * C + 1, y * C + 1, C - 2, C - 2, 5, '#2f7a3a');
          c.fillStyle = '#4fae58';
          c.beginPath();
          c.arc(x * C + 6, y * C + 6, 3, 0, Math.PI * 2);
          c.fill();
        }
      }));
      for (const k of dots) {
        const [x, y] = k.split(',').map(Number);
        c.fillStyle = `rgba(255,250,170,${0.6 + 0.4 * Math.sin(t * 4 + x + y)})`;
        c.beginPath();
        c.arc(x * C + 9, y * C + 9, 2.2, 0, Math.PI * 2);
        c.fill();
      }
      for (const k of flowers) {
        const [x, y] = k.split(',').map(Number);
        for (let i = 0; i < 5; i++) {
          const a = (i / 5) * Math.PI * 2 + t;
          c.fillStyle = '#ff9fe0';
          c.beginPath();
          c.arc(x * C + 9 + Math.cos(a) * 4, y * C + 9 + Math.sin(a) * 4, 3, 0, Math.PI * 2);
          c.fill();
        }
        c.fillStyle = '#fff6a8';
        c.beginPath();
        c.arc(x * C + 9, y * C + 9, 2.5, 0, Math.PI * 2);
        c.fill();
      }
      for (const f of frogs) {
        const fx = f.x * C + 9, fy = f.y * C + 10;
        c.fillStyle = sleep > 0 ? '#7a9ad0' : '#57c278';
        c.beginPath();
        c.ellipse(fx, fy, 7, 6, 0, 0, Math.PI * 2);
        c.fill();
        for (const s of [-1, 1]) {
          c.fillStyle = '#ffffff';
          c.beginPath();
          c.arc(fx + s * 3.5, fy - 5, 2.6, 0, Math.PI * 2);
          c.fill();
          c.fillStyle = '#1b1420';
          if (sleep > 0) c.fillRect(fx + s * 3.5 - 1.5, fy - 5, 3, 0.8);
          else c.fillRect(fx + s * 3.5 - 0.8, fy - 6, 1.6, 2);
        }
        if (sleep > 0) text(c, 'z', fx + 7, fy - 9 - Math.sin(t * 3) * 2, 8, '#ffffff');
      }
      // The firefly: glowing tail, little wings.
      const fx = px * C + 9, fy = py * C + 9;
      const glow = c.createRadialGradient(fx, fy + 2, 0, fx, fy + 2, 14);
      glow.addColorStop(0, 'rgba(240,255,140,0.75)');
      glow.addColorStop(1, 'rgba(240,255,140,0)');
      c.fillStyle = glow;
      c.fillRect(fx - 14, fy - 12, 28, 28);
      const flap = Math.sin(t * 30) * 2;
      c.fillStyle = 'rgba(220,240,255,0.8)';
      c.beginPath();
      c.ellipse(fx - 4, fy - 3 - flap * 0.3, 4, 2.5 + flap * 0.4, -0.5, 0, Math.PI * 2);
      c.ellipse(fx + 4, fy - 3 - flap * 0.3, 4, 2.5 + flap * 0.4, 0.5, 0, Math.PI * 2);
      c.fill();
      c.fillStyle = '#3a2a1a';
      c.beginPath();
      c.ellipse(fx, fy - 1, 3, 3.5, 0, 0, Math.PI * 2);
      c.fill();
      c.fillStyle = '#f6ff9a';
      c.beginPath();
      c.ellipse(fx, fy + 3, 3, 3, 0, 0, Math.PI * 2);
      c.fill();
      text(c, `${'✨'.repeat(lives)}  ${dots.size}`, W / 2, ROWS * C + 11, 12, '#f6ff9a');
    },
    stars: (s) => Math.min(6, Math.floor(s / 250)),
  };
  return g;
}

// ---------------------------------------------------------------------------
// Sketchbook: free drawing, shareable on PixelBook
// ---------------------------------------------------------------------------
function drawing(): ArcadeGame {
  const W = 300, H = 300;
  const paper = document.createElement('canvas');
  paper.width = W * 2;
  paper.height = H * 2;
  const pc = paper.getContext('2d')!;
  pc.scale(2, 2);
  let color = '#2a1810', size = 4, down = false, last: { x: number; y: number } | null = null;
  const clear = () => {
    pc.fillStyle = '#fffdf6';
    pc.fillRect(0, 0, W, H);
  };
  const s = S();
  const t0 = m().arcade.drawing;
  const prompt = t0.prompts[(s.dayIndex + s.week) % t0.prompts.length];
  const g: ArcadeGame = {
    w: W, h: H, buttons: [], score: 0, over: false,
    reset() {
      clear();
      g.score = 0;
      g.over = false;
    },
    pointer(type, x, y) {
      if (type === 'down') {
        down = true;
        last = { x, y };
      }
      if (type === 'up') {
        down = false;
        last = null;
      }
      if (down && last) {
        pc.strokeStyle = color;
        pc.lineWidth = size;
        pc.lineCap = 'round';
        pc.beginPath();
        pc.moveTo(last.x, last.y);
        pc.lineTo(x, y);
        pc.stroke();
        last = { x, y };
        g.score = Math.min(999, g.score + 1);
      }
    },
    update() {},
    draw(c) {
      c.drawImage(paper, 0, 0, W, H);
      text(c, `${t0.prompt} ${prompt}`, W / 2, 14, 11, '#7a5a3a');
    },
    extra() {
      const t = m().arcade.drawing;
      const colors = ['#2a1810', '#e2493b', '#ff9f43', '#ffd34e', '#57c278', '#3a8fd9', '#9b5fc0', '#ff7aa0', '#8a5530', '#ffffff'];
      return h(
        'div',
        { class: 'draw-tools' },
        h('div', { class: 'palette' }, colors.map((col) => h('button', { class: 'swatch-btn', style: `background:${col}`, 'aria-label': col, onclick: () => (color = col) }))),
        h(
          'div',
          { class: 'chips' },
          [2, 4, 8, 14].map((sz) => h('button', { class: 'chip small', onclick: () => (size = sz) }, '●'.repeat(Math.ceil(sz / 4)))),
          h('button', { class: 'chip small', onclick: () => ((color = '#fffdf6'), (size = 12)) }, t.eraser),
          h('button', { class: 'chip small', onclick: clear }, t.clear),
        ),
        h(
          'button',
          {
            class: 'btn green wide',
            onclick: () => {
              const st = S();
              st.posts.push({ id: Date.now(), img: paper.toDataURL('image/jpeg', 0.8), caption: -1, likes: 3, comments: [], day: st.dayIndex, text: `🎨 ${prompt}` } as never);
              st.posts = st.posts.slice(-12);
              if (onceToday('drawingShared')) {
                celebrate();
                apply({ stars: 4, mood: 8 });
              }
              toast(t.shared);
            },
          },
          t.share,
        ),
      );
    },
    stars: () => 0,
  };
  return g;
}

defineGame('rhythm', rhythm);
defineGame('circus', circus);
defineGame('snake', snake);
defineGame('minesweeper', minesweeper);
defineGame('match3', match3);
defineGame('maze', maze);
defineGame('drawing', drawing);
defineExternal('puzzle', openPuzzle);
defineExternal('blocks', openBlocks);
